/**
 * Phase 7B: Web Push — client helpers for opt-in subscribe/revoke.
 * Authority: WEB_PUSH_ARCHITECTURE.md, NOTIFICATION_CANON.md.
 */

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;

function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const raw = atob(base64.replace(/-/g, "+").replace(/_/g, "/") + padding);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

/** Register service worker at /sw.js. Returns registration or null. */
export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!("serviceWorker" in navigator)) return null;
  try {
    const reg = await navigator.serviceWorker.register("/sw.js", { scope: "/" });
    return reg;
  } catch (e) {
    console.warn("[push] service worker registration failed:", e);
    return null;
  }
}

/** Fetch VAPID public key from edge function (GET push-subscribe). */
export async function getVapidPublicKey(): Promise<string | null> {
  try {
    const res = await fetch(`${SUPABASE_URL}/functions/v1/push-subscribe`);
    if (!res.ok) return null;
    const json = await res.json();
    return json.publicKey ?? null;
  } catch {
    return null;
  }
}

/** Request permission, subscribe with VAPID key, and POST subscription to push-subscribe. Returns true on success. */
export async function subscribeToPush(
  authToken: string,
  options: { property_id?: string | null; severity_min?: string } = {}
): Promise<{ ok: boolean; error?: string }> {
  const reg = await registerServiceWorker();
  if (!reg) return { ok: false, error: "Service worker not supported or failed to register" };

  const permission = await Notification.requestPermission();
  if (permission !== "granted") return { ok: false, error: "Permission denied" };

  const publicKey = await getVapidPublicKey();
  if (!publicKey) return { ok: false, error: "Web Push not configured" };

  try {
    const sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    });
    const subscription = sub.toJSON();
    const endpoint = subscription.endpoint;
    const p256dh = subscription.keys?.p256dh ?? "";
    const auth = subscription.keys?.auth ?? "";
    if (!endpoint || !p256dh || !auth) return { ok: false, error: "Invalid subscription" };

    const res = await fetch(`${SUPABASE_URL}/functions/v1/push-subscribe`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authToken}`,
      },
      body: JSON.stringify({
        endpoint,
        p256dh,
        auth,
        property_id: options.property_id ?? null,
        user_agent: navigator.userAgent.slice(0, 500),
        severity_min: options.severity_min ?? "warning",
      }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) return { ok: false, error: data.error ?? `HTTP ${res.status}` };
    return { ok: true };
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    return { ok: false, error: msg };
  }
}

/** Get current push subscription for this device (from SW). */
export async function getCurrentPushSubscription(): Promise<PushSubscription | null> {
  if (!("serviceWorker" in navigator)) return null;
  try {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    return sub;
  } catch {
    return null;
  }
}

/** Revoke subscription on server (POST push-subscribe with revoke: true) and unsubscribe locally. */
export async function revokePushSubscription(authToken: string): Promise<{ ok: boolean; error?: string }> {
  const sub = await getCurrentPushSubscription();
  if (!sub) return { ok: true };

  const endpoint = sub.endpoint;
  try {
    await fetch(`${SUPABASE_URL}/functions/v1/push-subscribe`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authToken}`,
      },
      body: JSON.stringify({ revoke: true, endpoint }),
    });
    await sub.unsubscribe();
    return { ok: true };
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    return { ok: false, error: msg };
  }
}
